import {
  type default as React,
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { cn, type CurrencyCode, sanitizeHTML } from '@dinewise/utils';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { resolveCurrencyFieldOptions } from './currency-field.ts';

type BaseInputProps = Omit<ComponentProps<'input'>, 'className'>;

type InputCustomizations = {
  label?: string | React.ReactNode;
  labelPosition?: 'default' | 'floating' | 'above';
  description?: string;
  leftAccessory?: React.ReactNode;
  rightAccessory?: React.ReactNode;
  removeChevronAccessory?: boolean;
  transformValue?: (value: string) => string;
  allowHtmlInError?: boolean;
  classNames?: {
    group?: string;
    checkboxContainer?: string;
    radioContainer?: string;
    input?: string;
    errorInput?: string;
    errorIcon?: string;
    errorDescription?: string;
    leftAccessory?: string;
    rightAccessory?: string;
    description?: string;
    label?: string;
  };
};

export const dateInputElementClass = [
  'h-12 w-full rounded-md border-0 bg-white py-1.5 text-ande-black shadow-sm ring-0',
  'sm:text-base sm:leading-6 cursor-pointer focus:outline-none',
  '[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0',
  '[&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full',
  '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
  '[&::-webkit-calendar-picker-indicator]:[background-position:right_0.75rem_center]',
  '[&::-webkit-calendar-picker-indicator]:[background-size:1.2em_1.2em]',
  '[&::-webkit-calendar-picker-indicator]:[filter:brightness(0.7)]',
].join(' ');

export type RHFInputProps<FV extends FieldValues, Name extends FieldPath<FV>> = BaseInputProps &
  InputCustomizations & {
    control: Control<FV>;
    name: Name;
  };

type CheckboxProps = BaseInputProps & InputCustomizations & { error?: string };

const RHFCheckbox = (props: CheckboxProps) => {
  const {
    classNames,
    error,
    label,
    description,
    disabled,
    checked: inputChecked,
    value,
    ref,
    ...inputProps
  } = props;

  return (
    <div
      className={cn('group flex flex-col items-start', classNames?.group, error && 'input-error')}
    >
      <div
        className={cn(
          'relative flex gap-3 rounded-lg border border-transparent p-2 has-[:focus-visible]:border-ande-black group-[.input-error]:has-[:focus-visible]:border-ande-error',
          classNames?.checkboxContainer,
        )}
      >
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            {...inputProps}
            {...{ disabled, ref }}
            // oxlint-disable-next-line react/checked-requires-onchange-or-readonly -- Controlled by react-hook-form input props.
            checked={!!inputChecked || value?.toString() === 'true'}
            className={cn(
              "before:content=[''] before:-translate-y-1/2 before:-translate-x-1/2 size-5 rounded border-surface-950 text-surface-950 shadow-xl transition-opacity before:absolute before:top-1/2 before:left-1/2 before:block before:h-9 before:w-9 before:rounded-full before:bg-gray-500 before:opacity-0 before:transition-opacity focus:outline-none focus:ring-0 focus:ring-transparent focus-visible:outline-none active:enabled:before:opacity-20 disabled:border-gray-500 disabled:text-gray-500 group-[.input-error]:border-ande-error sm:hover:enabled:before:opacity-10",
              classNames?.input,
            )}
          />
        </div>
        {label ? (
          <label
            htmlFor={props.name}
            className={cn(
              'flex items-center text-ande-black text-sm',
              'group-has-[:disabled]:text-gray-500',
              classNames?.label,
            )}
          >
            {label}
          </label>
        ) : null}
        {description ? (
          <p className="text-ande-gray-500 text-sm" id={`${props.name}-description`}>
            {description}
          </p>
        ) : null}
      </div>
      {error ? (
        <p
          className={cn('py-0.5 pl-2 text-[10px] text-ande-error', classNames?.errorDescription)}
          id={`${props.name}-error`}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

type RadioProps = BaseInputProps &
  InputCustomizations & {
    fieldValue?: string;
  };

const RHFRadio = (props: RadioProps) => {
  const { classNames, label, description, value: radioValue, fieldValue, ...inputProps } = props;
  const checked = fieldValue === radioValue;

  return (
    <div className={cn('flex flex-col gap-2', classNames?.group)}>
      {label ? (
        <label
          htmlFor={props.id ?? props.name}
          className={cn('block pb-1 font-medium text-ande-black text-sm', classNames?.label)}
        >
          {label}
        </label>
      ) : null}
      <div className="flex flex-1 flex-col justify-center">
        <input
          {...inputProps}
          type="radio"
          value={radioValue}
          // oxlint-disable-next-line react/checked-requires-onchange-or-readonly -- Controlled by react-hook-form input props.
          checked={checked}
          className={cn(
            'size-6 rounded-full text-navy-500 ring-0 focus:ring-0',
            classNames?.radioContainer,
          )}
        />
      </div>
      {description ? (
        <p className="px-1 pt-2 text-gray-500 text-sm" id={`${props.name}-description`}>
          {description}
        </p>
      ) : null}
    </div>
  );
};

export const RHFInput = <FV extends FieldValues, Name extends FieldPath<FV>>(
  props: RHFInputProps<FV, Name>,
) => {
  const {
    classNames,
    leftAccessory,
    rightAccessory,
    label,
    description,
    labelPosition = 'default',
    control,
    name,
    type,
    transformValue,
    allowHtmlInError = false,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    ...inputProps
  } = props;
  const { field, fieldState } = useController<FV, Name>({ name, control });
  const error = fieldState.error?.message;
  const [showRightAccessory, setShowRightAccessory] = useState(true);

  // @ts-expect-error https://react-hook-form.com/faqs#Howtosharerefusage
  useImperativeHandle(field.ref, () => inputProps.ref?.current);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (type === 'search') {
        setShowRightAccessory(event.target.value.length === 0);
      }

      if (transformValue) {
        event.target.value = transformValue(event.target.value);
      }

      field.onChange(event);
      onChangeProp?.(event);
    },
    [field, onChangeProp, transformValue, type],
  );

  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlurProp?.(event); // allow onBlur handler to update event.target.value
      field.onChange(event); // commit data to hook form
      field.onBlur();
    },
    [field, onBlurProp],
  );

  const id = props.id ?? props.name;

  if (props.type === 'hidden') {
    return <input {...props} />;
  }

  if (props.type === 'checkbox') {
    return (
      <RHFCheckbox
        {...inputProps}
        {...field}
        {...{ label, onChange, error, id, name, description, classNames }}
      />
    );
  }

  if (props.type === 'radio') {
    const radioValue = inputProps.value;
    const fieldValue = field.value;

    return (
      <RHFRadio
        {...inputProps}
        onChange={onChange}
        value={radioValue}
        fieldValue={fieldValue}
        {...{ error, id, name, description, classNames }}
      />
    );
  }

  const showTrailingErrorIcon = type !== 'date';
  const errorInputClassName =
    classNames?.errorInput ??
    cn(
      'border-ande-error-light text-ande-error placeholder-ande-error-light focus:border-ande-error focus:outline-none focus:ring-ande-error',
      showTrailingErrorIcon && 'pr-10',
    );

  return (
    <div className={cn('group', classNames?.group, error && 'error')}>
      {label && labelPosition === 'default' ? (
        <label
          htmlFor={props.name}
          className={cn(
            'block pb-1 font-medium text-ande-gray-500 text-sm',
            'group-has-[:disabled]:text-ande-gray-300',
            classNames?.label,
          )}
        >
          {label}
        </label>
      ) : null}
      <div className="relative rounded-md">
        {leftAccessory ? (
          <div
            className={cn(
              classNames?.leftAccessory ??
                'pointer-events-none absolute inset-y-0 left-0 flex items-center',
            )}
          >
            {leftAccessory}
          </div>
        ) : null}
        <input
          {...inputProps}
          {...field}
          value={field.value ?? ''}
          {...{ onBlur, onChange, id }}
          type={type ?? 'text'}
          className={cn(
            'block w-full rounded-md sm:text-base',
            'disabled:cursor-not-allowed disabled:bg-ande-surface-100 disabled:text-ande-gray-300',
            leftAccessory && 'pl-6',
            rightAccessory && showRightAccessory && 'pr-6',
            classNames?.input ??
              'border-ande-gray-300 focus:border-ande-black focus:outline-none focus:ring-ande-black',
            !!error && errorInputClassName,
            label && labelPosition !== 'default' && 'peer',
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `${props.name}-error` : `${props.name}-description`}
        />
        {label && labelPosition !== 'default' ? (
          <label
            htmlFor={props.name}
            className={cn(
              'absolute top-1 transform cursor-text bg-white px-2 text-ande-black peer-disabled:text-ande-gray-300',
              labelPosition === 'floating' &&
                '-translate-y-4 peer-focus:-translate-y-4 peer-placeholder-shown:-translate-y-1/2 absolute left-1 z-10 origin-[0] scale-75 transform px-2 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-focus:top-1 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-gray-800',
              labelPosition === 'above' &&
                '-translate-y-7 peer-focus:-translate-y-4 peer-placeholder-shown:-translate-y-1/2 left-0 origin-[0] scale-75 bg-transparent px-1 text-xs duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-gray-800',
              classNames?.label,
            )}
          >
            {label}
          </label>
        ) : null}
        {showRightAccessory && rightAccessory && !error ? (
          <div
            className={cn(
              classNames?.rightAccessory ??
                'pointer-events-none absolute inset-y-0 right-0 flex items-center p-2',
            )}
          >
            {rightAccessory}
          </div>
        ) : null}
        {error && showTrailingErrorIcon ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className={cn('size-5 text-ande-error', classNames?.errorIcon)}
              aria-hidden="true"
            />
          </div>
        ) : null}
      </div>
      {error ? (
        <p
          className={cn('px-1 py-2 text-ande-error text-sm', classNames?.errorDescription)}
          id={`${props.name}-error`}
          {...(allowHtmlInError
            ? { dangerouslySetInnerHTML: { __html: sanitizeHTML(error) } }
            : { children: error })}
        />
      ) : null}
      {!error && !!description ? (
        <p
          className={cn('px-1 pt-2 text-ande-gray-500 text-sm', classNames?.description)}
          id={`${props.name}-description`}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};

type PhoneCustomInputProps = BaseInputProps & {
  id: string;
  error?: string;
};

const PhoneCustomInput = (props: PhoneCustomInputProps) => {
  const { error, name, ...inputProps } = props;
  const inputClassName =
    'border-ande-gray-300 focus:border-ande-black focus:outline-none focus:ring-ande-black';

  return (
    <input
      {...inputProps}
      className={cn('block w-full rounded-md sm:text-base', inputClassName)}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : `${name}-description`}
    />
  );
};

type PhoneNumberInputProps<FV extends FieldValues, Name extends FieldPath<FV>> = BaseInputProps & {
  control: Control<FV>;
  name: Name;
  label?: string;
  description?: string;
  /**
   * Country names for the selector. `react-phone-number-input` defaults to
   * English, so localized screens pass the pack for the active locale.
   */
  labels?: ComponentProps<typeof PhoneInput>['labels'];
  classNames?: {
    group?: string;
    errorDescription?: string;
    description?: string;
  };
};

export const RHFPhoneNumberInput = <FV extends FieldValues, Name extends FieldPath<FV>>(
  props: PhoneNumberInputProps<FV, Name>,
) => {
  const { id: inputId, control, label, name, classNames, description, placeholder, labels } = props;
  const id = inputId ?? name;
  const { field, fieldState } = useController<FV, Name>({ name, control });
  const error = fieldState.error?.message;

  return (
    <div className={classNames?.group}>
      {label ? (
        <label htmlFor={props.name} className="block pb-1 font-medium text-ande-black text-sm">
          {label}
        </label>
      ) : null}
      <input {...{ name }} value={field.value} type="hidden" />
      <PhoneInput
        {...{ placeholder, labels }}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={field.value}
        numberInputProps={{ id }}
        inputComponent={PhoneCustomInput}
        defaultCountry="US"
      />
      {error ? (
        <p
          id={`${name}-error`}
          className={cn('p-1 text-ande-error text-xs', classNames?.errorDescription)}
        >
          {error}
        </p>
      ) : null}
      {!error && !!description ? (
        <p
          id={`${name}-description`}
          className={cn('p-1 text-ande-gray-500 text-xs', classNames?.description)}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};

type DecimalInputProps<FV extends FieldValues, Name extends FieldPath<FV>> = Omit<
  RHFInputProps<FV, Name>,
  'type'
> & {
  decimals?: number;
};

export const RHFDecimalInput = <FV extends FieldValues, Name extends FieldPath<FV>>(
  props: DecimalInputProps<FV, Name>,
) => {
  const { onBlur, step, decimals = 2, min, placeholder, ...inputProps } = props;

  return (
    <RHFInput
      {...inputProps}
      type="text"
      pattern="\d+\.?\d*"
      placeholder={placeholder ?? (decimals ? `0.${'0'.repeat(decimals)}` : '0')}
      transformValue={value => value.replace(/[-$£,]/g, '')}
      onBlur={e => {
        // Ensure commas are removed before parsing
        const value = Number.parseFloat(e.target.value.replace(/,/g, ''));
        const valueToSet = Number.isNaN(value) ? '' : value.toFixed(decimals);
        e.target.value = valueToSet;
        onBlur?.(e);
      }}
    />
  );
};

export const RHFCurrencyInput = <FV extends FieldValues, Name extends FieldPath<FV>>(
  props: DecimalInputProps<FV, Name> & {
    currencyCode?: CurrencyCode;
    showCurrencySymbol?: boolean;
  },
) => {
  const {
    currencyCode = 'USD',
    decimals,
    showCurrencySymbol = true,
    leftAccessory,
    ...rest
  } = props;
  const currencyField = resolveCurrencyFieldOptions(currencyCode, decimals);
  const resolvedLeftAccessory =
    leftAccessory !== undefined ? (
      leftAccessory
    ) : showCurrencySymbol ? (
      <span className="pl-3 text-sm group-[.error]:text-ande-error-light">
        {currencyField.currencySymbol}
      </span>
    ) : undefined;

  return (
    <RHFDecimalInput
      {...rest}
      decimals={currencyField.decimals}
      leftAccessory={resolvedLeftAccessory}
    />
  );
};

export const RHFPercentInput = <FV extends FieldValues, Name extends FieldPath<FV>>(
  props: DecimalInputProps<FV, Name>,
) => {
  return (
    <RHFDecimalInput
      {...props}
      rightAccessory={
        <span className="pr-2 group-[.disabled]:text-ande-gray-300 group-[.error]:text-ande-error-light">
          %
        </span>
      }
    />
  );
};
